import { css, html, nothing } from 'lit';
import { state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import { CheckoutStep, CheckoutStepTemplate } from './checkout-step';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import {
  calculateTax,
  getDigitalHandlingFees,
  getShippingMethod,
  isShippingAddressValid,
  getBillingAddress,
  getBraintreeClientToken,
  getClientTokenResponse,
  getPaymentDetails,
  getPaymentMethod,
  getTotalPrice,
  getTotalTax,
  getPoOrderNumber,
  updateCountryCodeCurrencyCode,
  getEditBillingAddressMode,
  isNextStepButtonDisabled,
  updateSelectedPaymentType,
  getRemovedPaymentMethod,
  validPaymentDetails,
  isPurchaseOrderApplied
} from './shop-checkout';
import {
  BusinessUnit,
  CardTypes,
  ClientTokenDetails,
  ClientTokenRequest,
  PaymentDetails,
  PaymentType,
  RmsBookstoreAddress,
  } from './shop-checkout.interface';
import { getCart, getProductCountryCode, getProductCurrencyCode, isK12CustomStoreCart } from '../cart/shop-cart/store/shop-cart.selectors';
import { PaymentMethod, PaymentMethodType } from '../payment-methods/payment-method';

import paymentStyles from '../../common/styles/payment-styles';
import cardLogoStyles from '../../common/styles/card-logo-styles';
import sortBy from 'lodash/sortBy';
import {
  getCardLogo,
  renderCompletedAddress,
  renderCreditCardDisplay,
  render3rdPartyPaymentCardInfo,
  renderPaypalDisplay,
  renderVenmoDisplay,
  resetPurchaseOrderNumber,
} from '../../common/utils/payment-util';
import { getUserDataForUserAnalytics } from '../../features/user-profile/user-profile.selectors';
import { getDiscount } from '../../features/promo-code/promo-code.selectors';
import { waitForElementsToLoad } from '../../common/utils/delay-util';
import '../payment-methods/apple-pay/apple-pay-payment-method';
import '../payment-methods/credit-card/credit-card-payment-method';
import '../payment-methods/google-pay/google-pay-payment-method';
import '../payment-methods/paypal/paypal-payment-method';
import '../payment-methods/po-order/po-order-payment-method';
import '../payment-methods/venmo/venmo-payment-method';
import { addAnalyticsCheckoutFlowMetadata, hasHeProduct, hasMhpProduct } from '../../common/utils/cart-util';
import { MessageType } from '../alert/global-message.interface';
import { loading, MessageTemplateId } from '../../common/constants/api-constants';
import { isDesktop } from '../../common/utils/util';
import variables from '../../common/styles/variables';
import { isApplePaySupported } from '../payment-methods/apple-pay/utils/apple-pay.util';
import { Modal } from '../modal/modal';
import global from '../../common/styles/global';
import { PAYMENT_MESSAGE_ID, updateGlobalMessage } from '../alert/global-message';
import { ShopCart } from '../cart/shop-cart/interfaces/shop-cart.interface';
import '../payment-methods/ideal/ideal-payment-method';

/**
 * Checkout step to load payment options and capture payment details.
 */
export class PaymentBillingCheckoutStepBase extends connect(store)(CheckoutStep) implements CheckoutStepTemplate {
  static styles = [
    variables,
    paymentStyles,
    cardLogoStyles,
    global,
    css`
      .payment-method-option-wrapper.selected {
        background-color: var(--theme-cyan-100);
        border-color: var(--mh-light-blue) !important;
      }

      .payment-method-option-wrapper .payment-method-option:checked[type=radio] {
        border: none;
        background-image: url(/uxt/static/external/icons/check-circle-fill.svg);
        background-color: var(--mh-white);
      }

      .payment-method-option-wrapper .payment-method-option:focus[type=radio] {
        border-color: var(--mh-gray-2);
        outline-color: var(--mh-black) !important;
      }

      .payment-method-option-wrapper {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        padding-top: var(--mh-spacing-small);
        padding-bottom: var(--mh-spacing-small);
      }

      .purchase-order-link {
        padding: 0.75rem;
      }

      @media (max-width: 576px) {
        .payment-method-option-wrapper {
          flex-direction: column;
          align-items: flex-start;
        }
        .purchase-order-link {
          padding-top: 0;
          padding-left: 1rem;
          padding-bottom: 0.5rem;
        }
      }
    `
  ];

  /**
   * Currency code of the order. Default USD.
   */
  @state()
  protected currencyCode = 'USD';

  /**
   * Country code of the order. Default US.
   */
  @state()
  protected countryCode = 'US';

  /**
   * Total amount of the order.
   */
  @state()
  protected totalAmount = 0;

  /**
   * Flag to indicate shippable order.
   */
  @state()
  protected shippable = false;

  /**
   * Flag to indicate PO Order payment enabled.
   */
  @state()
  protected showPoOrderPaymentOption = false;

  @state()
  protected selectedPaymentMethodType: PaymentMethodType | undefined;

  @state()
  protected renderEditBillingAddressMode = false;

  /**
   * Selected payment method element.
   */
  protected selectedPaymentMethodEl: PaymentMethod | undefined;

  /**
   * Business unit of the products in the cart.
   */
  protected orderBusinessUnit: BusinessUnit | undefined;

  @state()
  protected availablePaymentOptions: PaymentMethodType[] = [];

  @state()
  protected showNextStepApplePayPayment = false;

  @state()
  protected showNextStepGooglePayPayment = false;

  @state()
  protected showNextStepPaypalPayment = false;

  @state()
  protected showNextStepVenmoPayment = false;

  @state()
  private modal?: Modal;

  @state()
  private disablePurchaseOrderOption = false;

  @state()
  private removedPaymentMethod: string | undefined;

  @state()
  protected idealPaymentMethodSelected = false;

  connectedCallback() {
    super.connectedCallback();
    this.addTrackedEventListener(window, 'free-trial-country-restriction-modal-close', this.focusOnSelectedOption.bind(this));
  }

  parentStateChanged(state: StoreState): void {
    this.updateTotalPrice(state);
    this.idealPaymentMethodSelected = this.isPaymentSuccess(PaymentMethodType.IDEAL, PaymentType.BRAINTREE_IDEAL, state);
  }

  /**
   * Focuses and re-selects the selected payment method option.
   *
   * @private
   */
  protected focusOnSelectedOption() {
    const selector = 'input[name="payment-method-option"]:checked';
    waitForElementsToLoad(this.shadowRoot!, [selector]).then(() => {
      const selectedPaymentMethod = this.shadowRoot?.querySelector(selector) as HTMLInputElement;

      if (selectedPaymentMethod) {
        selectedPaymentMethod.focus();
        selectedPaymentMethod.checked = true;
      }
    });
  }

  async updated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(_changedProperties);
    if (_changedProperties.has('selectedPaymentMethodType')) {
      store.dispatch(updateSelectedPaymentType(this.selectedPaymentMethodType?.type));
    }
  }

  async forceCompleteStep() {
    store.dispatch(
      updateCountryCodeCurrencyCode({
        currencyCode: this.currencyCode,
        countryCode: this.countryCode,
      })
    );
    if (isShippingAddressValid(store.getState() as StoreState) && getBillingAddress(store.getState() as StoreState)) {
      await store.dispatch(calculateTax()).unwrap();
    }

    if (this.isActive) {
      const state = store.getState() as StoreState;

      if (validPaymentDetails(state)) {
        this.isActive = false;
      } else if (this.availablePaymentOptions.length < 1) {
        const cart = getCart(state);
        if (cart) {
          this.loadPaymentOptions(cart, state);
          return;
        }
      }
    }

    this.isLoading = false;
  }

  handleEditBillingAddress(state: StoreState) {
    this.renderEditBillingAddressMode = getEditBillingAddressMode(state);
  }

  /**
   * Disables next step button if payment method is not selected or billing address is invalid.
   *
   * For performance reasons, The check is only done if the billing checkout step is active.
   */
  updateNextStepButton(state: StoreState): void {
    if (!this.isActive) {
      return;
    }
    this.isNextStepButtonDisabled = !this.selectedPaymentMethodType || isNextStepButtonDisabled(state);
  }

  /**
   * On load payment checkout step, get client token details and update payment context.
   */
  loadStep() {
    this.availablePaymentOptions = [];
    const state = store.getState() as StoreState;
    const cart = getCart(state);
    if (cart) {
      this.currencyCode = getProductCurrencyCode(state);
      this.countryCode = getProductCountryCode(state);
      store.dispatch(
        updateCountryCodeCurrencyCode({
          currencyCode: this.currencyCode,
          countryCode: this.countryCode,
        })
      );
      this.shippable = cart.shippable;
      this.totalAmount = cart.totalAmount;

      this.loadPaymentOptions(cart, state);
    }
  }

  /**
   * Load payment options based on the cart details.
   *
   * @private
   */
  private loadPaymentOptions(cart: ShopCart, state: StoreState): void
  {
    this.showPoOrderPaymentOption = cart.showPoOption || !!cart.isPurchaseOrder;

    /**
     * To disable Purchase Order option in Payment & Billing Section (If cart contains HE or MHP Product for SEG User Business Unit)
     */
    this.disablePurchaseOrderOption = (cart.userBusinessUnit === BusinessUnit.SEG) && (hasHeProduct(cart?.products) || hasMhpProduct(cart?.products));

    if (this.showPoOrderPaymentOption) {
      this.availablePaymentOptions = [...this.availablePaymentOptions, PaymentMethodType.PURCHASE_ORDER];
    }
    this.orderBusinessUnit = cart.orderBusinessUnit as BusinessUnit;
    const clientTokenRequest: ClientTokenRequest = {
      currencyCode: this.currencyCode,
      countryCode: this.countryCode,
      businessUnit: this.orderBusinessUnit!
    };

    const payload = getClientTokenResponse(state);
    if (payload) {
      this.updatePaymentContext(payload);
    } else {
      store
        .dispatch(getBraintreeClientToken(clientTokenRequest))
        .unwrap()
        .then((payload) => {
          this.updatePaymentContext(payload!);
        })
        .catch((err) => {
          console.error('failed to load payment and billing step.', err);
          store.dispatch(updateGlobalMessage({
            id: PAYMENT_MESSAGE_ID,
            message: MessageTemplateId.PAYMENT_METHODS_LOAD_ERROR,
            type: MessageType.primary
          }));
        })
    }
  }

  updatePaymentContext(payload: ClientTokenDetails) {
    const isK12CustomStore = isK12CustomStoreCart(store.getState() as StoreState);
    this.isLoading = false;
    this.availablePaymentOptions = [...this.availablePaymentOptions, PaymentMethodType.CREDIT_CARD];

    if (this.isApplePayEnabled(payload) && !isK12CustomStore) {
      this.availablePaymentOptions = [...this.availablePaymentOptions, PaymentMethodType.APPLE_PAY];
    }

    if (this.isGooglePayEnabled(payload) && !isK12CustomStore) {
      this.availablePaymentOptions = [...this.availablePaymentOptions, PaymentMethodType.GOOGLE_PAY];
    }

    if (this.isPaypalEnabled(payload) && !isK12CustomStore) {
      this.availablePaymentOptions = [...this.availablePaymentOptions, PaymentMethodType.PAYPAL];
    }

    if (this.isIdealPayEnabled(payload) && !isK12CustomStore) {
      this.availablePaymentOptions = [...this.availablePaymentOptions, PaymentMethodType.IDEAL];
    }

    const clientTokenResponse: ClientTokenDetails | undefined = getClientTokenResponse(store.getState() as StoreState);
    if (isDesktop() && clientTokenResponse?.venmoEnabled && clientTokenResponse?.venmoProfileId) {
      this.availablePaymentOptions = [...this.availablePaymentOptions, PaymentMethodType.VENMO];
    }

    if (this.availablePaymentOptions.length > 1) {
      this.availablePaymentOptions = sortBy(this.availablePaymentOptions, (paymentOption) => paymentOption.order);
    }
    
    this.initSelectedPaymentMethodType();
  }

  initSelectedPaymentMethodType() {
    const paymentType = getPaymentMethod(store.getState() as StoreState);
    if (paymentType) {
      this.selectPreviouslySelectedPaymentOption();
    } else {
      this.selectedPaymentMethodType = (this.showPoOrderPaymentOption && !this.disablePurchaseOrderOption)
        ? PaymentMethodType.PURCHASE_ORDER
        : PaymentMethodType.CREDIT_CARD;
    }
  }

  /**
   * Check if Paypal payment should be enabled.
   */
  protected isPaypalEnabled(_payload: ClientTokenDetails): boolean {
    throw new Error('Method not implemented.');
  }

  /**
   * Check if Apple Pay payment should be enabled.
   */
  protected isApplePayEnabled(_payload: ClientTokenDetails): boolean {
    return _payload.applePayEnabled && isApplePaySupported();
  }

  /**
   * Check if Google Pay payment should be enabled.
   */
  protected isGooglePayEnabled(_payload: ClientTokenDetails): boolean {
    return _payload.googlePayEnabled;
  }

  /**
   * Check if iDEAL Pay payment should be enabled.
   */
  protected isIdealPayEnabled(_payload: ClientTokenDetails): boolean {
    return !!_payload.idealEnabled && !!_payload.idealMerchantId;
  }

  protected isPaymentSuccess(
    paymentMethodType: PaymentMethodType,
    paymentType: PaymentType,
    state: StoreState,
  ) {
    return this.selectedPaymentMethodType?.type === paymentMethodType.type
      && !!getPaymentDetails(state)?.paymentNonce
      && getPaymentDetails(state)?.paymentType === paymentType;
  }

  /**
   * Render available payment methods.
   * @returns
   */
  renderCheckoutStep() {
    if (this.idealPaymentMethodSelected && !getBillingAddress(store.getState() as StoreState)) {
      return this.renderSelectedPaymentSummary();
    } else if (this.availablePaymentOptions.length > 1) {
      return this.renderPaymentOptions();
    } else if (this.availablePaymentOptions.length === 1) {
      return this.renderSinglePaymentOption();
    }
    return html``;
  }

  renderSelectedPaymentSummary() {
    return html`
      <div class="d-flex flex-column px-0">
        ${this.renderPaymentOption(this.selectedPaymentMethodType!, true)}
      </div>
      ${this.renderPaymentMethod()}
    `;
  }

  renderPaymentOptions() {
    return html`
      <div class="mb-4" role="radiogroup" aria-labelledby="paymentMethods">
        <p id="paymentMethods" class="mb-2 fw-semibold">Select a Payment Method</p>
        <div class="d-flex flex-column flex-wrap px-0">
          ${this.availablePaymentOptions.map(
            (paymentOption: PaymentMethodType) => this.renderPaymentOption(paymentOption)
          )}
        </div>
      </div>
      ${this.renderPaymentMethod()}
    `;
  }

  renderSinglePaymentOption() {
    const paymentOption = this.availablePaymentOptions[0];
    this.selectedPaymentMethodType = PaymentMethodType.getPaymentMethodType(paymentOption.type);
    const paymentMethodTemplate = this.getPaymentMethodTemplate(paymentOption.type);
    return html`
      <div class="d-flex flex-column px-0">
        <p>${this.renderPaymentOption(paymentOption)}</p>
      </div>
      <div class="my-4">
        ${paymentMethodTemplate}
      </div>
    `;
  }

  _handlePurchaseOrderModal() {
    const content = html`
      <p class="p-1">
        Unfortunately, a Purchase Order cannot be used to pay for Higher Education products. Please remove the Higher Education products from your cart or select a different payment method
      </p>
    `
    this.modal = new Modal({
      modalContext: {componentRef: 'purchaseOrderModal'},
      header: "Why can't I use a purchase order number?", 
      body: {template: content},
      disableFooter: true
    });
    this.modal.open();
  }

  /**
   * Render for only Purchase Order payment method.  
   * @param hasPurchaseOrderOption 
   * @returns 
   */

  renderPurchaseOrderLink(hasPurchaseOrderOption: boolean) {
    return html`
      ${(this.disablePurchaseOrderOption && hasPurchaseOrderOption)
        ? html `
          <button 
            class="float-end btn btn-link fs-small ml-3 purchase-order-link"
            id="purchaseOrderLink"
            @click=${this._handlePurchaseOrderModal}
            aria-label="Opens in a modal"
          >
            Why can't I use a purchase order number?
          </button>
      ` : nothing } `
  }

  /**
   * On select payment method radio option, update selected payment method type.
   * @returns payment radio button option. If payment method is selected, show payment method.
   */
  renderPaymentOption(paymentMethod: PaymentMethodType, hideFromA11y = false) {
    const isSelected = this.selectedPaymentMethodType?.type === paymentMethod.type;
    const isPurchaseOrderOption: boolean = paymentMethod.type === PaymentType.PURCHASE_ORDER;
    const hasDisabledPurchaseOption = this.disablePurchaseOrderOption && isPurchaseOrderOption;
    return html`
      <div
        class="
          border-2 ${hasDisabledPurchaseOption ? 'border-opacity-70' : 'border'} rounded-3 mt-2 payment-method-option-wrapper px-3 form-check-label
          ${isSelected ? 'selected' : ''} ${isDesktop() ? 'visibility' : ''}
        "
      >
        <input
          id="${paymentMethod.type}"
          class="form-check-input payment-method-option"
          type="radio"
          name="payment-method-option"
          .checked=${isSelected}
          aria-checked=${hideFromA11y ? false : isSelected}
          tabindex=${hideFromA11y ? '-1' : '0'}
          aria-hidden=${hideFromA11y}
          ?disabled=${hasDisabledPurchaseOption}
          @change=${() => this.selectPreviouslySelectedPaymentOption(paymentMethod)}
        />
        <label
          class="flex ${hasDisabledPurchaseOption ? 'cursor-default' : 'cursor-pointer'}"
          for="${paymentMethod.type}"
          aria-hidden=${hideFromA11y}
        >
          ${hasDisabledPurchaseOption ? nothing : html`<span class="visually-hidden">${paymentMethod.displayName}</span>`}
          ${this.useLogo(paymentMethod.type)
            ? html`${this._getCardImg(paymentMethod.type)}`
            : html`<span class="ms-1 ${hasDisabledPurchaseOption ? 'opacity-70' : ''}" aria-hidden="true">${paymentMethod.displayName}</span>`}
        </label>
        ${this.renderPurchaseOrderLink(isPurchaseOrderOption)}
      </div>
    `;
  }

  useLogo(type: PaymentType) {
    return [PaymentMethodType.PAYPAL.type, PaymentMethodType.VENMO.type, PaymentMethodType.GOOGLE_PAY.type, PaymentMethodType.APPLE_PAY.type].includes(type);
  }

  _getCardImg(type: PaymentType) {
    switch (type) {
      case PaymentMethodType.APPLE_PAY.type:
        return html`
          <img src="${getCardLogo(CardTypes.APPLE_PAY)}" aria-hidden="true" class="mx-1 bg-gradient" alt="${CardTypes.APPLE_PAY}">
        `;
      case PaymentMethodType.GOOGLE_PAY.type:
        return html`
          <img src="${getCardLogo(CardTypes.GOOGLE_PAY)}" aria-hidden="true" class="mx-1 bg-gradient" alt="${CardTypes.GOOGLE_PAY}">
        `;
      case PaymentMethodType.PAYPAL.type:
        return html`
          <img src="${getCardLogo(CardTypes.PAYPAL)}" aria-hidden="true" class="mx-1 bg-gradient paypalLogo" alt="${CardTypes.PAYPAL}">
        `;
      case PaymentMethodType.VENMO.type:
        return html`
          <img src="${getCardLogo(CardTypes.VENMO)}" aria-hidden="true" class="mx-1 bg-gradient venmoLogo" alt="${CardTypes.VENMO}">
        `;
      default:
        return nothing;
    }
  }

  /**
   * @returns The html for the selected payment method.
   */
  renderPaymentMethod() {
    return html`
      ${this.selectedPaymentMethodType
        ? html`
            ${this.braintreeHostedFieldsErrorMessage
              ? html`
                  <div class="my-3">
                    <mhecomm-alert
                      type=${MessageType.danger}
                      message=${this.braintreeHostedFieldsErrorMessage}
                    ></mhecomm-alert>
                  </div>
                `
              : nothing}
            <div id="${this.selectedPaymentMethodType.type}-container" class="payment-method-container">
              <div class="${this.idealPaymentMethodSelected ? 'my-0' : 'my-4'}">
                ${this.getPaymentMethodTemplate(this.selectedPaymentMethodType.type)}
              </div>
            </div>
          `
        : nothing}
      ${this.renderRemovePaymentToast()}
    `;
  }

  /**
   * @param paymentMethodType The type of payment method to render.
   * @returns The payment method html based on the provided payment method type.
   */
  getPaymentMethodTemplate(paymentMethodType: string) {
    if (paymentMethodType === PaymentMethodType.CREDIT_CARD.type) {
      return html`
        <mhecomm-credit-card-payment-method
          id="payment-method"
          totalAmount="${this.totalAmount}"
          countryCode="${this.countryCode}"
          ?shippable=${this.shippable}
        >
        </mhecomm-credit-card-payment-method>
        ${!this.renderEditBillingAddressMode ? html` <div>${this.renderNextStepButton()}</div> ` : nothing}
      `;
    } else if (paymentMethodType === PaymentMethodType.PAYPAL.type) {
      return html`
        <mhecomm-paypal-payment-method
          id="payment-method"
          totalAmount="${this.totalAmount}"
          ?shippable=${this.shippable}
          currency="${this.currencyCode}"
        >
        </mhecomm-paypal-payment-method>
        <div>${this.showNextStepPaypalPayment ? this.renderNextStepButton() : nothing}</div>
      `;
    } else if (paymentMethodType === PaymentMethodType.PURCHASE_ORDER.type && !this.disablePurchaseOrderOption) {
      return html`
        <mhecomm-po-order-payment-method
          id="payment-method"
          ?shippable=${this.shippable}
          totalAmount="${this.totalAmount}"
        >
        </mhecomm-po-order-payment-method>
        <div>${this.renderNextStepButton()}</div>
      `;
    } else if (paymentMethodType === PaymentMethodType.VENMO.type) {
      return html`
        <mhecomm-venmo-payment-method id="payment-method" totalAmount="${this.totalAmount}">
        </mhecomm-venmo-payment-method>
        <div>${this.showNextStepVenmoPayment ? this.renderNextStepButton() : nothing}</div>
      `;
    } else if (paymentMethodType === PaymentMethodType.GOOGLE_PAY.type) {
      return html`
        <mhecomm-google-pay-payment-method
          id="payment-method"
          totalAmount=${this.totalAmount}
          currency="${this.currencyCode}"
          ?shippable=${this.shippable}>
        </mhecomm-google-pay-payment-method>
        <div>${this.showNextStepGooglePayPayment ? this.renderNextStepButton() : nothing}</div>
      `
    } else if (paymentMethodType === PaymentMethodType.APPLE_PAY.type) {
      return html`
        <mhecomm-apple-pay-payment-method
          id="payment-method"
          totalAmount="${this.totalAmount}"
          currency=${this.currencyCode}
          countryCode="${this.countryCode}"
          ?shippable=${this.shippable}
        >
        </mhecomm-apple-pay-payment-method>
        <div>${this.showNextStepApplePayPayment ? this.renderNextStepButton() : nothing}</div>
      `;
    } else if (paymentMethodType === PaymentMethodType.IDEAL.type) {
      return html`
        <mhecomm-ideal-payment-method
          id="payment-method"
          totalAmount="${this.totalAmount}"
        >
        </mhecomm-ideal-payment-method>
        <div>${this.idealPaymentMethodSelected ? this.renderNextStepButton() : nothing}</div>
      `;
    } else {
      return nothing;
    }
  }

  renderLoadingTemplate() {
    return html`
      <span class="visually-hidden">Loading payment and billing step</span>
      ${loading({ columns: 4, m: 'mt-3' })} ${loading({ width: '50%' })} ${loading({ width: '70%' })}
      ${loading({ width: '30%' })}
    `;
  }

  /**
   * On click continue to next checkout step, after successful validation, render selected payment method results.
   * @returns
   */
  renderCompletedStepResults() {
    const state = store.getState() as StoreState;
    const paymentDetails = getPaymentDetails(state);
    const billingAddress = getBillingAddress(state)!;

    if (!paymentDetails) {
      return html``;
    }

    switch (paymentDetails.paymentType) {
      case PaymentType.BRAINTREE_APPLE_PAY:
      case PaymentType.BRAINTREE_GOOGLE_PAY:
        return this.render3rdPartyPaymentResults(paymentDetails, billingAddress);
      case PaymentType.BRAINTREE_CREDIT_CARD:
        return this.renderCreditCardPaymentResults(paymentDetails, billingAddress);
      case PaymentType.PURCHASE_ORDER:
        return this.renderPurchaseOrderPaymentResults(getPoOrderNumber(state)!, billingAddress);
      case PaymentType.BRAINTREE_PAYPAL:
        return this.renderPaypalPaymentResults(paymentDetails, billingAddress);
      case PaymentType.BRAINTREE_VENMO:
        return this.renderVenmoPaymentResults(paymentDetails?.venmoUserName!, billingAddress);
      case PaymentType.BRAINTREE_IDEAL:
        return this.renderIdealPaymentResults(billingAddress);
    }

    return html``;
  }

  renderPaypalPaymentResults(paymentDetails: PaymentDetails, billingAddress: RmsBookstoreAddress) {
    return html`
      <div id="payment-method-details-container" class="d-flex flex-row align-items-center pb-4">
        ${renderPaypalDisplay(paymentDetails)}
      </div>
      ${billingAddress ? html`
        <div id="billing-address-details-container">
          <h3 class="fs-6"><b>Billing Address</b></h3>
          ${renderCompletedAddress(billingAddress)}
        </div>
      ` : nothing}
    `;
  }

  /**
   * Displays payment details for Apple Pay and Google Pay.
   *
   * @param paymentDetails
   * @param billingAddress
   */
  render3rdPartyPaymentResults(paymentDetails: PaymentDetails, billingAddress: RmsBookstoreAddress) {
    return html`
      <div id="payment-method-details-container" class="d-flex flex-row flex-wrap p-3 border rounded">
        ${render3rdPartyPaymentCardInfo(billingAddress, paymentDetails)}
      </div>
    `;
  }

  renderCreditCardPaymentResults(paymentDetails: PaymentDetails, billingAddress: RmsBookstoreAddress) {
    return html`
      <div id="payment-method-details-container" class="pb-4">
        ${renderCreditCardDisplay(paymentDetails.creditCardDisplayInfo!)}
      </div>
      <div id="billing-address-details-container">
        ${billingAddress ? renderCompletedAddress(billingAddress) : nothing}
      </div>
    `;
  }

  renderPurchaseOrderPaymentResults(poNumber: string, billingAddress: RmsBookstoreAddress) {
    return html`
      <div id="po-method-details-container" class="pb-4">
        Purchase Order: ${poNumber}
      </div>
      ${billingAddress ? renderCompletedAddress(billingAddress) : nothing}
    `;
  }

  renderVenmoPaymentResults(userName: string, billingAddress: RmsBookstoreAddress) {
    return html`
      <div id="payment-method-details-container" class="d-flex flex-row align-items-center pb-4">
        ${renderVenmoDisplay(userName!)}
      </div>
      <div id="billing-address-details-container">
        ${billingAddress ? renderCompletedAddress(billingAddress) : nothing}
      </div>
    `;
  }

  renderRemovePaymentToast() {
    const state = store.getState() as StoreState;
    const paymentDetails = getPaymentDetails(state);
    this.removedPaymentMethod = getRemovedPaymentMethod(state);
    return this.removedPaymentMethod !== undefined && paymentDetails === undefined ? html`
      <mhecomm-toast
        type="secondary"
        header="Payment Method Removed"
        .body="${this.removedPaymentMethod} successfully removed."
        ?autoHide="${false}"
        @toast-close="${() => this.removedPaymentMethod = undefined}"
      ></mhecomm-toast>
    ` : nothing
  }

  /**
   * On edit step, select chosen payment method and prepopulate billing address. Invalidate billing address in redux.
   */
  async editStepAction() {
    this.loadStep();
    this.focusOnSelectedOption();
  }

  selectPreviouslySelectedPaymentOption(paymentMethod?: PaymentMethodType) {
    const state = store.getState() as StoreState;
    const paymentDetails = getPaymentDetails(state);
    const poNumber = state.shopCartState.getShopCart.data?.poNumber;

    if (paymentMethod && paymentMethod?.type !== PaymentType.PURCHASE_ORDER && poNumber) {
      resetPurchaseOrderNumber();
    }
    
    if (paymentMethod) {
      this.selectedPaymentMethodType = paymentMethod;
    } else if (paymentDetails && paymentDetails.paymentType) {
      this.selectedPaymentMethodType = PaymentMethodType.getPaymentMethodType(paymentDetails.paymentType);
    }
    waitForElementsToLoad(this.shadowRoot!, [this.selectedPaymentMethodType?.component!]).then(() => {
      this.selectedPaymentMethodEl = this.shadowRoot!.querySelector(
        this.selectedPaymentMethodType?.component!
      ) as PaymentMethod;
      this.selectedPaymentMethodEl?.paymentMethodEditAction();
    });
  }

  /**
   * 
   * @param state Method used to make sure we have the total updated as
   * we can have promocode applied after the 3rd party payment methods
   * initialized
   */
  updateTotalPrice(state: StoreState) {
    const total = getTotalPrice(state);
      if (total) {
        this.totalAmount = total;
      }
  }

  checkoutStepCompleteDetails() {
    const state = store.getState() as StoreState;
    return addAnalyticsCheckoutFlowMetadata({
      id: this.id,
      user: getUserDataForUserAnalytics(state),
      paymentType: this.selectedPaymentMethodType?.displayName,
      totalTax: getTotalTax(state),
      orderTotal: getTotalPrice(state),
      serviceFee: getDigitalHandlingFees(state)?.amount,
      promoCode: getDiscount(state),
      shippingMethod: getShippingMethod(state)?.freightEstimateTypes.shipMethodDescription,
      comp: 'payment-billing-checkout-step',
      label: this.nextStepButtonName,
      url: window.location.href,
      ...(!!getPoOrderNumber(state) && isPurchaseOrderApplied(state) && 
      { 
        paymentType: "Purchase Order", 
        purchaseOrderNumber: getPoOrderNumber(state) 
      })
    });
  }

  renderIdealPaymentResults(billingAddress: RmsBookstoreAddress) {
    return html`
      <div id="payment-method-details-container" class="d-flex flex-row align-items-center pb-4">
        <div class="border border-2 border-secondary-subtle rounded me-2 p-1">
          <img src="/uxt/static/external/misc/logo_ideal_review_step.svg" alt="iDEAL Payment Method" aria-label="iDEAL Payment Method" >
        </div>
        <span>iDEAL Pay</span>
      </div>
      <div id="billing-address-details-container">
        ${billingAddress ? html`
          <h3 class="fs-6"><b>Billing Address</b></h3>
          ${renderCompletedAddress(billingAddress)}
        ` : nothing}
      </div>
    `;
  }
}
